import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  color: #1f2937;
  font-size: 24px;
`;

const BackButton = styled.button`
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 14px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
`;

const Button = styled.button`
  border: 1px solid #00b894;
  background: #00d4aa;
  color: #fff;
  border-radius: 8px;
  padding: 9px 12px;
`;

const Item = styled.div`
  font-size: 14px;
  color: #334155;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
`;

const Message = styled.div`
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
`;

export const IntegrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [apiKeyName, setApiKeyName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('auth_token');
  const headers = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }), [token]);

  const loadAll = useCallback(async () => {
    const [keysRes, hooksRes, partnersRes, eventsRes] = await Promise.all([
      fetch('/api/integrations/api-keys', { headers }),
      fetch('/api/integrations/webhooks', { headers }),
      fetch('/api/integrations/partners', { headers }),
      fetch('/api/integrations/events?per_page=10', { headers }),
    ]);
    const keysData = await keysRes.json();
    const hooksData = await hooksRes.json();
    const partnersData = await partnersRes.json();
    const eventsData = await eventsRes.json();
    setApiKeys(keysData.data || []);
    setWebhooks(hooksData.data || []);
    setPartners(partnersData.data || []);
    setEvents((eventsData?.data?.data || []) as any[]);
  }, [headers]);

  useEffect(() => {
    loadAll().catch(() => setMessage('Não foi possível carregar integrações para este plano.'));
  }, [loadAll]);

  const createApiKey = async () => {
    const res = await fetch('/api/integrations/api-keys', {
      method: 'POST',
      headers,
      body: JSON.stringify({ name: apiKeyName }),
    });
    const data = await res.json();
    if (!res.ok) {
      setMessage(data.message || 'Erro ao criar chave.');
      return;
    }
    setMessage(`API Key criada: ${data.data?.api_key}`);
    setApiKeyName('');
    await loadAll();
  };

  const createWebhook = async () => {
    const res = await fetch('/api/integrations/webhooks', {
      method: 'POST',
      headers,
      body: JSON.stringify({ url: webhookUrl }),
    });
    const data = await res.json();
    if (!res.ok) {
      setMessage(data.message || 'Erro ao criar webhook.');
      return;
    }
    setMessage('Webhook cadastrado com sucesso.');
    setWebhookUrl('');
    await loadAll();
  };

  const createPartner = async () => {
    const res = await fetch('/api/integrations/partners', {
      method: 'POST',
      headers,
      body: JSON.stringify({ partner_company_id: Number(partnerId) }),
    });
    const data = await res.json();
    if (!res.ok) {
      setMessage(data.message || 'Erro ao criar parceiro.');
      return;
    }
    setMessage('Solicitação de parceiro criada.');
    setPartnerId('');
    await loadAll();
  };

  return (
    <Container>
      <Header>
        <Title>Integrações e Ecossistema</Title>
        <BackButton onClick={() => navigate('/dashboard')}>Voltar ao Dashboard</BackButton>
      </Header>

      {message && <Message>{message}</Message>}

      <Grid>
        <Card>
          <h3>Nova API Key</h3>
          <Input value={apiKeyName} onChange={(e) => setApiKeyName(e.target.value)} placeholder="Nome da chave" />
          <Button onClick={createApiKey}>Criar</Button>
        </Card>
        <Card>
          <h3>Novo Webhook</h3>
          <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://sua-url/webhook" />
          <Button onClick={createWebhook}>Cadastrar</Button>
        </Card>
        <Card>
          <h3>Novo Parceiro</h3>
          <Input value={partnerId} onChange={(e) => setPartnerId(e.target.value)} placeholder="ID da empresa parceira" />
          <Button onClick={createPartner}>Conectar</Button>
        </Card>
      </Grid>

      <Card>
        <h3>API Keys</h3>
        {apiKeys.map((item) => (
          <Item key={item.id}>
            {item.name} | ativa: {item.is_active ? 'sim' : 'não'} | último uso: {item.last_used_at || '-'}
          </Item>
        ))}
      </Card>

      <Card>
        <h3>Webhooks</h3>
        {webhooks.map((item) => (
          <Item key={item.id}>
            {item.url} | falhas: {item.failure_count ?? 0} | último status: {item.last_status_code || '-'}
          </Item>
        ))}
      </Card>

      <Card>
        <h3>Parceiros</h3>
        {partners.map((item) => (
          <Item key={item.id}>
            {item.partner_company?.name || `Empresa #${item.partner_company_id}`} | status: {item.status}
          </Item>
        ))}
      </Card>

      <Card>
        <h3>Eventos Recentes</h3>
        {events.map((item) => (
          <Item key={item.id}>
            #{item.id} | {item.type} | status: {item.status} | idem: {item.idempotency_key || '-'}
          </Item>
        ))}
      </Card>
    </Container>
  );
};
